/** A photo for the card scan (STORY-089 M3): the teacher's phone photographs the raised cards.
 *
 * A file input with `capture`, not a live camera: it needs no secure context, so it works on
 * the Teacher Computer app's `http://<lan-ip>:7420` and on staging over plain HTTP, where
 * `getUserMedia` is absent (a browser rule, checked on phones by hand), and a camera photo has
 * several times the pixels of a video frame, which the back row needs.
 *
 * Null in the desktop shell (a webcam scan is its own, later item) and in both Capacitor shells
 * until open question 6 of the story is settled: the Android shell's file chooser keeps every
 * capture as a JPEG in the app's own storage and nothing deletes it, and the iOS shells carry
 * no camera usage string. There the teacher scans from the phone's browser.
 *
 * Offered only where the primary pointer is coarse (`(pointer: coarse)`: a phone or a tablet),
 * where a camera capture is plausible. A laptop's live view is often mirrored to the projector,
 * and there the action would open a file dialog in front of the class (the owner's decision of
 * 2026-10-09).
 *
 * The page never uploads, logs or stores the photo: the caller decodes it on the phone and
 * drops it. What the browser does with a capture before handing it over is the platform's. */
import type { Platform } from "./capabilities";

export type CardCamera = {
  /** Opens the rear camera; must be called within the tap that asked. Resolves with the photo
   * when the input changes, or null when the picker is cancelled. A browser that fires no
   * `cancel` leaves the promise pending; each call starts afresh, so nothing waits on it. */
  photo(): Promise<Blob | null>;
};

/** The input's attributes: any image, the rear camera. */
export const CARD_PHOTO_INPUT = { accept: "image/*", capture: "environment" } as const;

/** Is the primary pointer coarse (a finger)? False where the query cannot be asked. */
export function coarsePointer(): boolean {
  try {
    return typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

let pending: HTMLInputElement | null = null;

function inputPhoto(): Promise<Blob | null> {
  // One input at a time: a picker that never said it was cancelled leaves its input behind.
  pending?.remove();
  const input = document.createElement("input");
  input.type = "file";
  input.accept = CARD_PHOTO_INPUT.accept;
  input.setAttribute("capture", CARD_PHOTO_INPUT.capture);
  input.hidden = true;
  pending = input;
  // In the document, because older Safari ignores a click on a detached input.
  document.body.appendChild(input);
  return new Promise((resolve) => {
    const done = (blob: Blob | null) => {
      input.remove();
      if (pending === input) pending = null;
      resolve(blob);
    };
    input.addEventListener("change", () => done(input.files?.[0] ?? null), { once: true });
    input.addEventListener("cancel", () => done(null), { once: true });
    input.click();
  });
}

export function cardCameraFor({ desktop, platform, coarse }: { desktop: boolean; platform: Platform; coarse: boolean }): CardCamera | null {
  if (desktop || platform !== "web" || !coarse) return null;
  return { photo: inputPhoto };
}
